import { TaskStateType } from ".";
import { ITag } from "../shared/db";
import { TimeSlotWithTaskRecord } from "../shared/mongo/localdate_schedule_time_slot";
import { TaskRecord } from "../shared/mongo/localdate_task_record";

export interface Adherence {
  credentials: {
    expiresInMilliseconds: number;
    expiresUnixTimestamp: number;
    loginCode: number;
    participantId: number;
  } | null;
  participantId: string;
  status: AdherenceStatus;
  digestDayList: DigestDay[];
  digestDayMap?: { [yymmdd: string]: DigestDay };
}

export interface AdherenceStatus {
  appVersion: string;
  batchId: string;
  deviceBatteryPercentage?: number;
  deviceBatteryPercentageTimestamp?: number;
  deviceFirmware: string;
  deviceMac: string;
  deviceType: string;
  deviceUUID: string | null;
  mobileOsVersion: string;
  mobileType: "android" | "ios";
  participantId: string;
  participantInsignia: string;
  projectId: string;
  timeOffesetToUTCInMs: any; // TODO: check type
  timezone: string;
  unixTimestampLastAppOpen: number;
  unixTimestampLastGarminDeviceSync: number;
  unixTimestampLastGarminConnectSync: number;
  _id: string;
}

type TaskStateTypeWithoutTypeKey = Omit<TaskStateType, "type">;
export interface TaskStateTypeWithoutGarmin extends TaskStateTypeWithoutTypeKey {
  type: "todo" | "timer" | "questionnaire";
}

export interface DigestDay {
  _id?: string;
  day: string;
  dayDate: string;
  dayUnixTimestamp: number;
  yymmddIndex: number;
  hasContent?: boolean | undefined;
  garminDirectCollected?: boolean;
  garminDirectAdherence?: AdherenceGarminDirect;
  garminConnectSynced?: boolean;
  dexcomSynced?: boolean;
  task?: { [key: string]: any };
  taskCompletionCountMap?: Record<string, number>;
  scheduleTaskStateMap?: { [taskId: string]: ScheduleTaskState };
}

export type DigestDayDetail = {
  task: { [key: string]: AdherenceNormalTask | AdherenceGarminTask };
  garminConnect?: AdherenceGarminConnect;
  dexcomDataCount?: number;
};

export type TaskRecordTimestamp = {
  unixTimestampComplete: number;
  timezone: string;
  timeOffset: number;
  unixTimestampStart?: number;
  completionId?: string;
};

export interface ScheduleTaskState {
  display?: boolean;
  timeSlotList?: TimeSlotWithTaskRecord[];
  totalCount?: number;
  completedCount?: number;
  multiDayTimeSlotIdList?: string[];
  multiDayTaskRecords?: TaskRecord[]; // got record means completed on this day
}

export interface AdherenceNormalTask {
  taskId: string;
  taskType:
    | "todo"
    | "questionnaire"
    | "timer"
    | "stopwatch_movesense_stream"
    | "stopwatch_movesense_log_data"
    | "stopwatch_garmin_stream"
    | "dexcom";
  timestampList: TaskRecordTimestamp[];
}

// BE TODO: duplicate key value, should update (ex. healthDailies & healthDailiesCollected)
export interface AdherenceGarminConnect {
  activityCollected: boolean;
  activityDetailsCollected: boolean;
  activityFilesCollected: boolean;
  activityMoveIQCollected: boolean;
  healthBloodPressureCollected: boolean;
  healthBodyCompositionsCollected: boolean;
  healthDailies: boolean;
  healthDailiesCollected: boolean;
  healthEpochs: boolean;
  healthEpochsCollected: boolean;
  healthHRVSummaryCollected: boolean;
  healthPulseOx: boolean;
  healthPulseOxCollected: boolean;
  healthRespiration: boolean;
  healthRespirationCollected: boolean;
  healthSleeps: boolean;
  healthSleepsCollected: boolean;
  healthSnapshotCollected: boolean;
  healthStress: boolean;
  healthStressCollected: boolean;
  healthSkinTemperatureCollected: boolean;
  healthThirdPartyDailiesCollected: boolean;
  healthUserMetricsCollected: boolean;
  womenHealthMCTCollected: boolean;
  synced: boolean;
}

export interface AdherenceGarminDirect {
  acc?: boolean;
  bbi?: boolean;
  enhancedBbi?: boolean;
  heartRate?: boolean;
  pulseOx?: boolean;
  respiration?: boolean;
  steps?: boolean;
  stress?: boolean;
  actigraphy?: boolean;
  zeroCrossing?: boolean;
  temperature?: boolean;
}

export interface AdherenceGarminTask extends AdherenceGarminDirect {
  taskId: string;
  taskType: "garmin_device";
  bbiMSAccumulated?: number;

  // bbiChunkCount: number
  // bbiIntegrityAccumulated: number
  // bbiIntegrityChunkAverage: number
  // bbiIntegrityDay: number
  // bbiMSDuration: number
  // bbiMSMissing: number
  // bbiSampleCount: number
  // bbiUnixTimestampFirst: number
  // bbiUnixTimestampLast: number
}

export type GarminDirect =
  | undefined
  | ""
  | "Heart Rate Interval (BBI)"
  | "Steps"
  | "Stress"
  | "Oxygen Saturation"
  | "Heart Rate"
  | "Respiration"
  | "Zero Crossings"
  | "Actigraphy"
  | "Skin Temperature";

export const GarminDirectBackendKeyMap: Record<string, keyof AdherenceGarminDirect> = {
  "Heart Rate Interval (BBI)": "bbi",
  Steps: "steps",
  Stress: "stress",
  "Oxygen Saturation": "pulseOx",
  "Heart Rate": "heartRate",
  Respiration: "respiration",
  "Zero Crossings": "zeroCrossing",
  Actigraphy: "actigraphy",
  "Skin Temperature": "temperature",
};

export interface SelectTaskListItem {
  id: string;
  name: string;
  color: string;
  index: number;
}

export interface HandleToggleTaskPayload {
  type: string;
  payload: {
    id?: string;
    name: string;
    color: string;
  };
}

export interface SidebarContent {
  digestDay?: DigestDay;
  yymmddIndex: number;
  garminConnectEnabled?: boolean;
  participantId: string;
  participantName: string;
  participantActivatedYymmdd?: number;
  status: AdherenceStatus;
  tagList: ITag[];
  dayCount: string;
  sidebarContentDayCount: string;
  taskCompletionStatus: TaskCompletionStatus[];
  notification: boolean;
  scheduleTaskRecordMap?: { [taskId: string]: TimeSlotWithTaskRecord[] };
  dexcomSynced?: boolean;
  dexcomEgvDataCount?: number;
  _id?: string;
}

export interface TaskCompletionStatus {
  display: boolean;
  name: string;
  index: number;
  color: string;
  completionCount: number;
  totalCount: number;
}

export interface ParticipantDigestDay {
  day: string;
  dayDate: string;
  dayUnixTimestamp: number;
  participantId: string;
  projectId: string;
  batchId: string;
  garminDirectCollected?: boolean;
  garminConnectSynced?: boolean;
  garminConnect?: AdherenceGarminConnect;
  dexcomSynced?: boolean;
  dexcomDataCount?: number;
  task: { [key: string]: AdherenceNormalTask | AdherenceGarminTask };
}

export interface AdherenceViewTag {
  id?: string;
  name: string;
  color: string;
  index?: number;
  alt?: string;
}
